import React from "react";
import { useGameSlugs, useGameStatistics, useMyEntitlements } from "@seabrookstudios/pitch2table";
import { useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import { useMakeListAndDetail, useMakeListBuilder } from "../core/useMakeLayout";
import { Path } from "../Path";
import { Empty } from "./Empty";
import Add from "./Add";
import Box from "@mui/material/Box";
import { ListEntry } from "./ListEntry";
import Typography from "@mui/material/Typography";
import { AlwaysOnAppLink } from "../styleguide/AppLink";
import Alert from "@mui/material/Alert";
import { GameVisibilityControl } from "../games/GameVisibilityControl";
import { GameVisibilityCreditsControl } from "./GameVisibilityCreditsControl";
import { GameFeedbackControl } from "../games/GameFeedbackControl";
import { getStatAmount } from "@seabrookstudios/pitch2table-core";
import { makeGameViewedById } from "@seabrookstudios/pitch2table-core";
import { LockedListItem } from "../core/HasEntitlements";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useHasSlug } from "../sessions/model";

const useHasGameSlugs = () => {
  const gameId = useGameIdFromRoute();
  const { data: gameSlugs = [] } = useGameSlugs(gameId);
  return gameSlugs.length > 0;
};

/**
 * Component for displaying total game views
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const GameViewsCard = ({ gameId }) => {
  const { data: entitlements } = useMyEntitlements();
  const { data: stats = [] } = useGameStatistics(gameId);

  const totalViews = getStatAmount(stats, makeGameViewedById(gameId), 0);

  if (!entitlements || !entitlements.isMember) {
    return <LockedListItem title="Total Views" />;
  }

  return (
    <ListItem>
      <ListItemText primary="Total Page Views" secondary={totalViews} />
    </ListItem>
  );
};

const SlugHeader = () => {
  const gameId = useGameIdFromRoute();
  const hasUserSlug = useHasSlug();
  const hasGameSlugs = useHasGameSlugs();

  if (!hasUserSlug) {
    return (
      <Box paddingBottom={1} paddingLeft={1} paddingRight={1}>
        <Alert severity="warning">
          <Typography variant="body2">
            No username set. Please set your username in{" "}
            <AlwaysOnAppLink to={Path.settings().category("about-you")}> settings</AlwaysOnAppLink> to use this feature.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box paddingBottom={1} paddingLeft={1} paddingRight={1}>
        {!hasGameSlugs && (
          <Alert severity="info">
            <Typography variant="body2">
              Add a game link to create a URL that can be shared with publishers. The URL will contain key information
              about your game and will include your latest rules and sellsheet.
            </Typography>
          </Alert>
        )}
      </Box>
      <GameVisibilityControl gameId={gameId} />
      <GameFeedbackControl gameId={gameId} />
      <GameVisibilityCreditsControl gameId={gameId} />
      <GameViewsCard gameId={gameId} />
    </Box>
  );
};

export const ListAndDetail = () => {
  const gameId = useGameIdFromRoute();
  const hasUserSlug = useHasSlug();
  const hasGameSlugs = useHasGameSlugs();

  const Action = () => <Add gameId={gameId} shouldDisable={!hasUserSlug || hasGameSlugs} />;

  const useMakeList = useMakeListBuilder(
    ListEntry,
    (item) => item,
    "Game Visibility",
    Empty,
    Action,
    SlugHeader,
    Path.game(gameId).link()
  );

  const useWrappedQuery = () => useGameSlugs(gameId);
  const ListAndDetail = useMakeListAndDetail(useWrappedQuery, [], "slugs", useMakeList, () => <Box />);

  return (
    <Box>
      <ListAndDetail />
    </Box>
  );
};
